import { Fragment, ReactNode } from 'react'
import ReactDOM from 'react-dom'
import { BaseEditor, Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useFocused, useSelected } from 'slate-react'

export const IS_MAC = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac OS X')

export type RenderElementPropsFor<T> = RenderElementProps & {
    element: T
}

export interface MentionElement {
    type: 'mention';
    character: string;
    children: CustomText[];
    id: string;
}

export interface CustomText {
    bold?: boolean
    italic?: boolean
    code?: boolean
    underline?: boolean
    strikethrough?: boolean
    // MARKDOWN PREVIEW SPECIFIC LEAF
    underlined?: boolean
    title?: boolean
    list?: boolean
    hr?: boolean
    blockquote?: boolean
    text: string
}

export type CustomEditor = BaseEditor &
ReactEditor & {
    nodeToDecorations?: Map<Element, Range[]>
}

export const Leaf = ({ attributes, children, leaf }: any) => { // RenderLeafProps
    if (leaf.bold) {
        children = <strong>{children}</strong>
    }

    if (leaf.code) {
        children = <code>{children}</code>
    }

    if (leaf.italic) {
        children = <em>{children}</em>
    }

    if (leaf.underline) {
        children = <u>{children}</u>
    }

    return <span {...attributes}>{children}</span>
}

export const Element = (props: any) => { // RenderElementProps
    const { attributes, children, element } = props
    switch (element.type) {
        case 'mention':
            return <Mention {...props} />
        default:
            return <p {...attributes}>{children}</p>
    }
}

export const Mention = ({
    attributes,
    children,
    element,
}: RenderElementPropsFor<MentionElement>) => {
    const selected = useSelected()
    const focused = useFocused()
    const style: React.CSSProperties = {
        padding: '3px 3px 2px',
        margin: '0 1px',
        verticalAlign: 'baseline',
        display: 'inline-block',
        borderRadius: '4px',
        backgroundColor: '#eee',
        fontSize: '0.9em',
        boxShadow: selected && focused ? '0 0 0 2px #B4D5FF' : 'none',
    }
    // See if our empty text child has any styling marks applied and apply those
    if (element.children[0].bold) {
        style.fontWeight = 'bold'
    }
    if (element.children[0].italic) {
        style.fontStyle = 'italic'
    }
    return (
        <span
            {...attributes}
            contentEditable={false}
            data-cy={`mention-${element.character.replace(' ', '-')}`}
            style={style}
        >
            {/* Prevent Chromium from interrupting IME when moving the cursor */}
            {/* 1. span + inline-block 2. div + contenteditable=false */}
            <div contentEditable={false}>
                {IS_MAC
                    ? (
                        <Fragment>
                            {children}@{element.character}
                        </Fragment>
                    )
                    : (
                        <Fragment>
                        @{element.character}
                            {children}
                        </Fragment>
                    )}
            </div>
        </span>
    )
}

export const withMentions = (editor: CustomEditor) => {
    const { isInline, isVoid, markableVoid } = editor

    editor.isInline = (element: any) => {
        return element.type === 'mention' ? true : isInline(element)
    }

    editor.isVoid = (element: any) => {
        return element.type === 'mention' ? true : isVoid(element)
    }

    editor.markableVoid = (element: any) => {
        return element.type === 'mention' || markableVoid(element)
    }

    return editor
}

export const createMention = (character: string, id: string): MentionElement => {
    return {
        type: 'mention',
        character,
        children: [{ text: '' }],
        id,
    }
}

export const insertMention = (editor: CustomEditor, name: string, id: string) => {
    Transforms.insertNodes(editor, createMention(name, id))
    Transforms.move(editor)
}

export const Portal = ({ children }: { children?: ReactNode }) => {
    return typeof document === 'object'
        ? ReactDOM.createPortal(children, document.body)
        : null
}
